import React, { useState } from 'react';
import { useGetQuery } from 'api/useGetQuery';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';

import { useDataEnvironment } from 'contexts/DataEnvironmentContext';
import DataEnvironmentNotSet from 'modules/DataEnvironmentNotSet/DataEnvironmentNotSet';

import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { DataEnvironment as DataEnvironmentType } from 'types/DataEnvironment';
import { toNumber } from 'utils/toNumber';
import { showError } from 'utils/showError';
import styled from 'styled-components';

const LoadingContainer = styled.div`
  height: calc(100vh - 78px);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-weight: bold;
`;

type Props = {
  children: JSX.Element | JSX.Element[];
};

export const DataEnvironmentGuard: React.FC<Props> = ({ children }: Props) => {
  const [dataEnvironment, setDataEnvironment] = useDataEnvironment();
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const { search, state } = useLocation();
  const params = queryString.parse(search);

  // <Auth/> stashes the dataEnvironmentId if one is present before anything else
  // b/c we noticed a 2x redirect where we start with the data env and then come
  // back with the jwt token
  const fromSession = toNumber(sessionStorage.getItem('dataEnvironmentId'));

  // Support query strings (might come into play later)
  const fromSearch = toNumber(params && params.dataEnvironmentId);

  // <Auth/> sets state from query params once authenticated
  const fromState = toNumber(((state as any) || {}).dataEnvironmentId);

  // "select" the apparent data environment by preference - route state > query param > session storage
  const dataEnvironmentId = fromState || fromSearch || fromSession;

  useGetQuery<DataEnvironmentType[]>('/user_data_environments', {
    onError: () => {
      //TODO: showError or throw error?
      showError('Unable to load Data Environments.');
    },
    onSuccess: (data: DataEnvironmentType[]) => {
      const dataEnv =
        data && dataEnvironmentId !== undefined
          ? data.find((environment) => environment.id === dataEnvironmentId)
          : undefined;

      sessionStorage.setItem('dataEnvironmentId', dataEnv ? `${dataEnv.id}` : '');

      setDataEnvironment(dataEnv);

      setTimeout(() => setIsInitializing(false), 800);
    },
  });

  if (isInitializing) {
    return (
      <LoadingContainer>
        <div>
          <LoadingIndicator size="lg" />
          <div className="mt-3">Launching</div>
        </div>
      </LoadingContainer>
    );
  }

  return dataEnvironment ? <>{children}</> : <DataEnvironmentNotSet />;
};

export default DataEnvironmentGuard;
